import { _ } from "../../polyglot/polyglot.js";
import { addMonths } from "../../helpers/date-util.js";
import { card, text } from "../../elements/index.js";
import { configureDialog } from "../dialogs/dialogs.js";
import { formatAsMonthAndYear } from "../../helpers/date-formatting-util.js";
import { notBlank } from "../forms/validators.js";
import { range } from "@foretagsplatsen/core-helpers/array";
import ManualJournalEntryRuleModel from "../../../models/forecast/ruleModels/ManualJournalEntryRuleModel.js";
import ManualJournalEntryTransaction from "../../../models/forecast/ManualJournalEntryTransaction.js";
import regionWidget from "../regionWidget.js";
import TransactionsStrategy from "./TransactionsStrategy/TransactionsStrategy.js";
import WidgetsListWidget from "../WidgetsListWidget/WidgetsListWidget.js";

const defaultEntryName = "";

/**
 * Editor to create or alter a manual journal entry.
 *
 * @param {Account[]} allAccounts - List of accounts that can be used
 *   to set the manual entry
 * @param {Account} [selectedAccount] - Account selected by default
 *   for the manual entry. Besides UI, this account has nothing special
 *   and should not be treated as such
 * @param {function(ManualJournalEntryRuleModel): void} [onSubmit] - Function invoked with a ManualJournalEntryRuleModel
 *   after the form has been validated and submitted
 * @param {ManualJournalEntryRuleModel} [manualJournalEntryRuleModel] - Rule model to edit
 *   in the scenario of altering an existing rule model
 * @param {function({transactions: ManualJournalEntryTransaction[]}): void} [extraValidator] - validator
 *   used to check the transactions. As other validators, the returned value is
 *   ignored, but the function should throw an error in case of validation
 *   failing
 * @param {Date} currentMonth - Month to apply the manual journal entry
 *   rule
 */
export default class ManualJournalEntryDialog {
	constructor({
		allAccounts,
		selectedAccount = allAccounts[0],
		onSubmit = () => {},
		manualJournalEntryRuleModel,
		extraValidator = () => {},
		currentMonth,
	}) {
		this._manualJournalEntryRuleModel = manualJournalEntryRuleModel;
		this._allAccounts = allAccounts;
		this._onSubmit = onSubmit;
		this._currentMonth = currentMonth;

		this._formManualJournalEntryRuleModel =
			this._makeManualJournalEntryRuleModel({
				selectedAccount,
			});

		this._errorRegion = regionWidget({ content: "" });

		this._balanceRederable = regionWidget({
			content: (html) => {
				const balance =
					this._formManualJournalEntryRuleModel.computeCurrentBalance();

				return html.span(
					{
						class: { unbalanced: balance !== 0 },
						"data-test": "balance-value",
					},
					balance,
				);
			},
		});

		this._extraValidator = extraValidator;

		this._dialog = configureDialog({
			title: _("Manual Journal"),
			subtitle: _("Moving amounts between accounts"),
			contentCssClass: "ManualJournalEntryDialog modal-overflow",
			formSpec: {
				klass: "main-form",
				model: this._formManualJournalEntryRuleModel,
				saveAction: this._submitForm.bind(this),
			},
			formBuilder: this._populateForm.bind(this),
			extraButtonsRenderer: () => [this._errorRegion],
		});
	}

	show() {
		this._dialog.show();
	}

	_makeManualJournalEntryRuleModel({ selectedAccount }) {
		return (
			this._manualJournalEntryRuleModel?.clone() ||
			this._makeDefaultManualJournalEntryRuleModel({ selectedAccount })
		);
	}

	_makeDefaultManualJournalEntryRuleModel({ selectedAccount }) {
		return new ManualJournalEntryRuleModel({
			name: defaultEntryName,
			transactions: [
				new ManualJournalEntryTransaction({ account: selectedAccount }),
				new ManualJournalEntryTransaction({
					account: undefined,
				}),
			],
		});
	}

	_submitForm(model) {
		if (this._manualJournalEntryRuleModel) {
			this._alterManualJournalEntryModel({
				manualJournalEntryRuleModel: this._manualJournalEntryRuleModel,
				model,
			});

			this._onSubmit(this._manualJournalEntryRuleModel);
			return;
		}

		this._onSubmit(model);
	}

	_alterManualJournalEntryModel({ manualJournalEntryRuleModel, model }) {
		manualJournalEntryRuleModel.apply(model);
	}

	_populateForm(form) {
		this._appendNameInput(form);
		this._appendNumberOfRollingMonthsInput(form);
		this._appendTransactions(form);
		this._appendBalance(form);
	}

	_appendNameInput(form) {
		form.input({
			label: _("Name of the journal"),
			name: "name",
			mutator: (name) =>
				this._formManualJournalEntryRuleModel.setName(name),
			accessor: () => this._formManualJournalEntryRuleModel.getName(),
			validator: notBlank(),
		});
	}

	_appendNumberOfRollingMonthsInput(form) {
		form.newDropDownField({
			dataTestName: "number-of-rolling-months",
			label: _("Rolling to"),
			liveSearch: false,
			noValidation: true, // as 0 is a valid value, and there is no empty selection
			items: this._makeMonths(),
			accessor: () =>
				this._formManualJournalEntryRuleModel.getNumberOfRollingMonths(),
			onChange: (value) => {
				this._formManualJournalEntryRuleModel.setNumberOfRollingMonths(
					value,
				);
			},
		});
	}

	_appendTransactions(form) {
		const transactionFields = this._makeTransactionFields();

		const renderableFieldWidget = form.addRenderable({
			content: transactionFields,
			showErrorExternally: true,
			accessor: () =>
				this._formManualJournalEntryRuleModel.getTransactions(),
			mutator: (transactions) =>
				this._formManualJournalEntryRuleModel.setTransactions(
					transactions,
				),
			getFormValue: () =>
				this._formManualJournalEntryRuleModel.getTransactions(),
			setFormValue: (transactions) =>
				this._formManualJournalEntryRuleModel.setTransactions(
					transactions,
				),
			validator: () => {
				const alertCollector = transactionFields.validate();
				alertCollector.assertEmpty();
			},
		});

		renderableFieldWidget.register(
			"validationStateChange",
			this._handleError.bind(this),
		);
	}

	_makeTransactionFields() {
		return new WidgetsListWidget({
			strategy: new TransactionsStrategy({
				allAccounts: this._allAccounts,
				manualJournalEntryRuleModel:
					this._formManualJournalEntryRuleModel,
				extraValidator: this._extraValidator,
				onInputChange: () => this._balanceRederable.update(),
			}),
		});
	}

	_handleError(_input, error) {
		if (!error) {
			this._errorRegion.setContent("");
			return;
		}

		this._errorRegion.setContent((html) =>
			html.span(
				{
					klass: "help-block has-error",
					"data-test": "error-footer",
				},
				error.message,
			),
		);
	}

	_makeMonths() {
		const items = [];

		const numberOfMonths = [...range(1, 13), ...range(24, 72, 12)];

		for (const currentNumberOfMonths of numberOfMonths) {
			const currentDate = addMonths(
				this._currentMonth,
				currentNumberOfMonths,
			);

			items.push({
				label: [
					formatAsMonthAndYear(currentDate),
					" - ",
					_(
						"One month",
						"{currentNumberOfMonths} months",
						currentNumberOfMonths,
						{
							currentNumberOfMonths,
						},
					),
				],
				value: currentNumberOfMonths,
			});
		}

		items.push({
			label: _("Infinite"),
			value: Infinity,
		});

		return items;
	}

	_appendBalance(form) {
		form.append((html) =>
			html.div(
				{ klass: "balance-container" },
				card(
					{ size: "none" },
					html.div(
						{
							klass: "balance-card",
							"data-test": "balance-card",
						},
						text(_("Discrepancy")),
						this._balanceRederable,
					),
				),
			),
		);
	}
}
